import { useLayoutEffect, useMemo, useRef, useState, useSyncExternalStore } from 'react';
import { ApiError, feedbackConversationAuthorityFingerprint, observeAuthTokenChanges, saveScope } from '@/lib/api';
import { queryClient, removeAllScopedFromCache, unscopedKey } from '@/lib/queryClient';
import { revokeAllPlayerPhotos } from '@/features/player-photos/usePlayerPhoto';
import { useAuthStore } from '@/stores/auth';
import { useScopeStore } from '@/stores/scope';

/** Observe departures synchronously, including A -> B -> A between renders.
 * This is a mounted UI lifetime, not another persisted scope or auth protocol. */
export function useScopeIdentity() {
  const observer = useMemo(() => {
    let snapshot = { authority: feedbackConversationAuthorityFingerprint(), generation: 0 };
    const getSnapshot = () => {
      const authority = feedbackConversationAuthorityFingerprint();
      if (authority !== snapshot.authority) snapshot = { authority, generation: snapshot.generation + 1 };
      return snapshot;
    };
    return { getSnapshot, subscribe: (notify: () => void) => {
      const changed = () => { getSnapshot(); notify(); };
      const auth = useAuthStore.subscribe(changed), scope = useScopeStore.subscribe(changed);
      const token = observeAuthTokenChanges(changed);
      changed();
      return () => { auth(); scope(); token(); };
    } };
  }, []);
  const snapshot = useSyncExternalStore(observer.subscribe, observer.getSnapshot, observer.getSnapshot);
  return useMemo(() => ({ ...snapshot, current: () => observer.getSnapshot() === snapshot }), [observer, snapshot]);
}

/** Save through the existing account preference. A departed caller may never
 * apply a late result, even if the same account/context subsequently returns. */
export function useScopeSelection() {
  const mounted = useRef(false);
  const pending = useRef<{ dispose(): void } | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  // HTTP status of the last failed save (null: none, or not an HTTP error),
  // so a caller can react to a 409 season/team mismatch.
  const failureStatus = useRef<number | null>(null);
  useLayoutEffect(() => {
    mounted.current = true;
    return () => { mounted.current = false; pending.current?.dispose(); pending.current = null; };
  }, []);

  const select = async (selection: Parameters<typeof saveScope>[0]): Promise<boolean> => {
    if (!mounted.current || pending.current) return false;
    const auth = useAuthStore.getState();
    if (!auth.user || auth.hydrating) return false;
    const actorId = auth.user.id, clubId = auth.currentClub?.id ?? null;
    const authority = feedbackConversationAuthorityFingerprint();
    let departed = false;
    const changed = () => { if (feedbackConversationAuthorityFingerprint() !== authority) departed = true; };
    const offAuth = useAuthStore.subscribe(changed), offScope = useScopeStore.subscribe(changed);
    const offToken = observeAuthTokenChanges(changed);
    const operation = { dispose: () => { departed = true; offAuth(); offScope(); offToken(); } };
    pending.current = operation;
    failureStatus.current = null;
    setPendingId(selection.season_id ?? selection.team_id); setError(null);
    try {
      const payload = await saveScope(selection);
      if (!mounted.current || departed || pending.current !== operation) return false;
      if (payload.user?.id !== actorId || (payload.current_club?.id ?? null) !== clubId
        || payload.active_scope?.team.id !== selection.team_id || payload.selection_required
        || (selection.season_id && payload.active_scope?.season?.id !== selection.season_id)) {
        throw new Error('The selected context is no longer available. Choose a current team and season.');
      }
      // All application is synchronous after the last lifetime/response check.
      operation.dispose();
      useAuthStore.getState().applyMePayload(payload);
      useScopeStore.getState().applyMePayload(payload);
      queryClient.setQueryData(unscopedKey('me'), payload);
      removeAllScopedFromCache();
      revokeAllPlayerPhotos();
      return true;
    } catch (failure) {
      failureStatus.current = failure instanceof ApiError ? failure.status : null;
      if (mounted.current && !departed && pending.current === operation)
        setError(failure instanceof Error ? failure.message : 'Could not switch context');
      return false;
    } finally {
      operation.dispose();
      if (pending.current === operation) {
        pending.current = null;
        if (mounted.current) setPendingId(null);
      }
    }
  };
  return {
    select,
    pendingId,
    isPending: pendingId !== null,
    error,
    lastFailureStatus: () => failureStatus.current,
  };
}
